/**
 * Scrubber, 2027 audit year and later: universe validation under the CMS-10717
 * program audit protocols CMS applies starting with the 2027 audit year (OMB
 * approval through 10/31/2029). Same house layout as the Scrubber
 * (title block, pill tabs, the work), strict by design: exact headers, exact
 * codes, no stray spaces, every rule reported with the consequence CMS
 * would attach to it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';
import { Card, Pill, Notice, StepChips, Stat, TabBar, Table, btn, input, label as labelCls } from '../Scrubber/ui';
import { PROTOCOLS, TABLE_PLAN, getJson, postForm, downloadBlob, fmtNum, todayIso } from './api';

const STEPS = ['Protocol', 'Table', 'Engagement', 'File', 'Results'];
const KIND_TONE = { error: 'rose', finding: 'amber', observation: 'sky' };
const KIND_LABEL = { error: 'Error', finding: 'Finding', observation: 'Observation' };
const RESULT_TONE = { pass: 'emerald', fail: 'rose', 'needs-input': 'violet', manual: 'slate', info: 'slate', deferred: 'slate', 'n/a': 'slate', 'not-run': 'amber', 'not-implemented': 'rose' };
const RESULT_LABEL = { pass: 'Pass', fail: 'Fail', 'needs-input': 'Needs input', manual: 'Case file review', info: 'Reminder', deferred: 'Needs other tables', 'n/a': 'Not applicable', 'not-run': 'Not run', 'not-implemented': 'Not implemented' };
const STATUS_LABEL = { implemented: 'Implemented', partial: 'Partly automated', 'needs-input': 'Needs outside data', manual: 'Case documentation', informational: 'Process reminder', 'cross-universe': 'Cross universe', 'not-applicable': 'Other table', 'not-implemented': 'Not implemented' };

function Dropzone({ onFile, busy }) {
  const [over, setOver] = useState(false);
  const take = (f) => {
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) { onFile(null, `"${f.name}" is not an .xlsx workbook. This version validates Excel universes only.`); return; }
    onFile(f, '');
  };
  return (
    <label onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files && e.dataTransfer.files[0]); }}
      className={`block rounded-2xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition ${over ? 'border-orange-400 bg-orange-50/60' : 'border-slate-200 bg-slate-50/70 hover:bg-slate-50 hover:border-orange-200'}`}>
      <input type="file" className="hidden" accept=".xlsx" disabled={busy} onChange={(e) => { take(e.target.files && e.target.files[0]); e.target.value = ''; }} />
      <div className="text-sm font-semibold text-slate-800">{busy ? 'Validating…' : 'Drop the universe workbook here, or click to choose it'}</div>
      <div className="text-xs text-slate-500 mt-1">Excel (.xlsx) only, exactly as it would be uploaded to HPMS. The file is validated in memory and is not stored by this step.</div>
    </label>
  );
}

function VerdictBanner({ result }) {
  const v = result.verdict;
  const map = {
    rejected: ['red', 'This universe would be rejected by CMS', `${fmtNum(result.counts.error)} record layout or data integrity error${result.counts.error === 1 ? '' : 's'} on ${fmtNum(result.rowsWithIssues)} row${result.rowsWithIssues === 1 ? '' : 's'}. CMS rejects submissions that do not strictly adhere to the record layout.`],
    findings: ['amber', 'Record layout clean, audit findings present', `${fmtNum(result.counts.finding)} case${result.counts.finding === 1 ? '' : 's'} would be cited under the timeliness or notice standards.`],
    observations: ['sky', 'Record layout clean, items to confirm', `${fmtNum(result.counts.observation)} observation${result.counts.observation === 1 ? '' : 's'} to confirm against source systems before submitting.`],
    clean: ['emerald', 'Clean', 'Every automated rule passed. Rules that need outside data or case documentation are listed below.'],
  };
  const [tone, title, body] = map[v] || ['slate', v, ''];
  return (
    <Notice tone={tone}>
      <div className="text-sm font-semibold">{title}</div>
      <div className="text-xs mt-0.5">{body}</div>
      {result.file.noData && <div className="text-xs mt-1">The file is a no-data universe (headers plus the CMS sentence).</div>}
      {result.file.rowsSkipped && <div className="text-xs mt-1">Row rules did not run because the header row does not match the layout; fix the headers and validate again.</div>}
    </Notice>
  );
}

function ContextForm({ ctx, setCtx, tierSets, table, onContinue }) {
  const tiers = (tierSets && tierSets[table?.tierSet]) || [];
  const needsTier = tiers.length > 0;
  const periodHint = {
    odag_main: 'Tables 1, 2, 4 and 5: 16, 12, 4 or 2 weeks preceding and including the letter date.',
    odag_payment: 'Table 3: 4 weeks (2 weeks at 500,000 and over) preceding and including the letter date.',
    cdag: '12, 8, 4 or 2 weeks preceding and including the letter date.',
    fa_rcfa: 'Rejected claims with dates of service in the 8, 4 or 2 weeks preceding and including the letter date.',
    fa_rct: 'Dates of service in January and February of the audit year (January only at 100,000 and over).',
    fa_ne: 'Effective enrollment dates from 11/1 of the previous year through 2/1 (1/1 at 100,000 and over) of the audit year.',
    snpcc: 'The letter date sets the 12 month new enrollee window, the 365 day continuing enrollee test and the 26 week and 3 year lookbacks.',
    cpe: 'The 6 month period preceding and including the letter date.',
  }[table?.periodKind] || '';
  return (
    <Card title="Engagement letter" subtitle="The universe period is counted back from the engagement letter date by enrollment tier, exactly as the protocol states it.">
      <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
        <div><label className={labelCls}>Engagement letter date</label><input type="date" className={input} value={ctx.engagementLetterDate} onChange={(e) => setCtx({ ...ctx, engagementLetterDate: e.target.value })} /></div>
        {needsTier && (
          <div>
            <label className={labelCls}>{table.tierLabel || 'Enrollment'} tier</label>
            <select className={input} value={ctx.enrollmentTier} onChange={(e) => setCtx({ ...ctx, enrollmentTier: e.target.value })}>
              <option value="">Select the tier</option>
              {tiers.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
        )}
        <div className="text-[11px] text-slate-500 sm:col-span-2">{periodHint}</div>
      </div>
      <div className="mt-4"><button type="button" className={btn.primary} disabled={!ctx.engagementLetterDate || (needsTier && !ctx.enrollmentTier)} onClick={onContinue}>Continue to the file</button></div>
    </Card>
  );
}

function RulesTable({ rules, filter, setFilter, showEvidence }) {
  const counts = useMemo(() => rules.reduce((a, r) => { a[r.result || r.status] = (a[r.result || r.status] || 0) + 1; return a; }, {}), [rules]);
  const keys = showEvidence ? Object.keys(STATUS_LABEL) : Object.keys(RESULT_LABEL);
  const list = rules.filter((r) => !filter || (showEvidence ? r.status : r.result) === filter);
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        <button type="button" onClick={() => setFilter('')} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${!filter ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>All {rules.length}</button>
        {keys.filter((k) => counts[k]).map((k) => (
          <button key={k} type="button" onClick={() => setFilter(filter === k ? '' : k)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${filter === k ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{(showEvidence ? STATUS_LABEL : RESULT_LABEL)[k]} {counts[k]}</button>
        ))}
      </div>
      <Table flat head={['Rule', 'Layer', 'CMS consequence', showEvidence ? 'Status' : 'Result', showEvidence ? 'Evidence' : 'Issues', 'What CMS checks']}>
        {list.map((r) => (
          <tr key={r.id} className="align-top">
            <td className="px-3 py-2 text-xs font-mono text-slate-700 whitespace-nowrap">{r.id}</td>
            <td className="px-3 py-2 text-xs text-slate-500">{r.layer}</td>
            <td className="px-3 py-2 text-xs text-slate-600">{r.severity}</td>
            <td className="px-3 py-2">{showEvidence ? <Pill tone={r.status === 'implemented' ? 'emerald' : r.status === 'partial' ? 'sky' : 'slate'}>{STATUS_LABEL[r.status]}</Pill> : <Pill tone={RESULT_TONE[r.result] || 'slate'}>{RESULT_LABEL[r.result] || r.result}</Pill>}</td>
            <td className="px-3 py-2 text-xs text-slate-600">{showEvidence ? (r.evidence || '') : (r.count ? fmtNum(r.count) : '')}</td>
            <td className="px-3 py-2 text-xs text-slate-700">{r.rule}{r.note && <div className="text-[11px] text-slate-500 mt-0.5">{r.note}</div>}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function IssuesTable({ issues, total }) {
  const [kind, setKind] = useState('');
  const [rule, setRule] = useState('');
  const [page, setPage] = useState(0);
  const PAGE = 200;
  const list = issues.filter((i) => (!kind || i.kind === kind) && (!rule || i.ruleId === rule));
  const rules = useMemo(() => [...new Set(issues.map((i) => i.ruleId))].sort(), [issues]);
  useEffect(() => setPage(0), [kind, rule]);
  const slice = list.slice(page * PAGE, page * PAGE + PAGE);
  return (
    <Card title={`Issues (${fmtNum(total)})`} subtitle="Every issue names the rule, the cell and what CMS requires. Errors reject the universe; findings become audit conditions; observations are for you to confirm.">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {['', 'error', 'finding', 'observation'].map((k) => (
          <button key={k || 'all'} type="button" onClick={() => setKind(k)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${kind === k ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{k ? KIND_LABEL[k] + 's' : 'All kinds'}</button>
        ))}
        <select className="rounded-xl border border-slate-200 px-2 py-1 text-xs text-slate-700 bg-white" value={rule} onChange={(e) => setRule(e.target.value)}>
          <option value="">All rules</option>
          {rules.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <span className="text-xs text-slate-500">{fmtNum(list.length)} shown{issues.length < total ? ` (list capped at ${fmtNum(issues.length)})` : ''}</span>
      </div>
      <Table flat head={['Row', 'Col', 'Field', 'Kind', 'Rule', 'Value', 'What CMS requires']}>
        {slice.map((i, n) => (
          <tr key={n} className="align-top">
            <td className="px-3 py-1.5 text-xs tabular-nums text-slate-700">{i.row}</td>
            <td className="px-3 py-1.5 text-xs text-slate-700">{i.col || ''}</td>
            <td className="px-3 py-1.5 text-xs text-slate-600">{i.field || ''}</td>
            <td className="px-3 py-1.5"><Pill tone={KIND_TONE[i.kind]}>{KIND_LABEL[i.kind]}</Pill></td>
            <td className="px-3 py-1.5 text-xs font-mono text-slate-600 whitespace-nowrap">{i.ruleId}</td>
            <td className="px-3 py-1.5 text-xs text-slate-800 max-w-[12rem] truncate" title={i.value || ''}>{i.value || ''}</td>
            <td className="px-3 py-1.5 text-xs text-slate-700">{i.message}</td>
          </tr>
        ))}
      </Table>
      {list.length > PAGE && (
        <div className="flex items-center gap-3 mt-3 text-xs text-slate-600">
          <button type="button" className={btn.secondarySmall} disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
          <span>Page {page + 1} of {Math.ceil(list.length / PAGE)}</span>
          <button type="button" className={btn.secondarySmall} disabled={(page + 1) * PAGE >= list.length} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </Card>
  );
}

function UniverseGrid({ result }) {
  const [onlyIssues, setOnlyIssues] = useState(true);
  const [limit, setLimit] = useState(100);
  const rows = (result.universe || []).filter((g) => !onlyIssues || g.cells.some((c) => c.issues.length) || g.timely === false);
  const headers = result.headers && result.headers.length ? result.headers : [];
  return (
    <Card title="Universe" subtitle={`${fmtNum(result.file.rows)} data rows${result.universeMode === 'issues-only' ? `; only rows with issues are shown (${fmtNum(result.universeRowsOmitted)} clean rows omitted)` : ''}. Hover a highlighted cell for the rule.`}
      right={<label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} /> Rows with issues only</label>}>
      <div className="overflow-auto max-h-[32rem] border border-slate-200 rounded-xl">
        <table className="min-w-full text-[11px]">
          <thead className="sticky top-0 bg-slate-50">
            <tr><th className="px-2 py-1.5 text-left font-semibold text-slate-600">Row</th><th className="px-2 py-1.5 text-left font-semibold text-slate-600">Timeliness</th>{headers.map((h, i) => <th key={i} className="px-2 py-1.5 text-left font-semibold text-slate-600 whitespace-nowrap" title={h}>{String.fromCharCode(65 + (i % 26)).padStart(i >= 26 ? 2 : 1, 'A')}</th>)}</tr>
          </thead>
          <tbody>
            {rows.slice(0, limit).map((g) => (
              <tr key={g.row} className="border-t border-slate-100">
                <td className="px-2 py-1 tabular-nums text-slate-600">{g.row}</td>
                <td className="px-2 py-1" title={g.verdict || ''}>{g.timely === true ? <Pill tone="emerald">Timely</Pill> : g.timely === false ? <Pill tone="rose">Untimely</Pill> : g.assessed === false ? <Pill tone="slate">n/a</Pill> : <Pill tone="amber">Unmeasured</Pill>}</td>
                {g.cells.map((c, i) => {
                  const worst = c.issues.some((x) => x.kind === 'error') ? 'bg-rose-100' : c.issues.some((x) => x.kind === 'finding') ? 'bg-amber-100' : c.issues.length ? 'bg-sky-100' : '';
                  return <td key={i} className={`px-2 py-1 whitespace-nowrap max-w-[14rem] truncate ${worst}`} title={c.issues.length ? c.issues.map((x) => `${x.ruleId}: ${x.message}`).join('\n') : c.v}>{c.v}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > limit && <button type="button" className={`${btn.secondarySmall} mt-3`} onClick={() => setLimit(limit + 200)}>Show more ({fmtNum(rows.length - limit)} more)</button>}
    </Card>
  );
}

function TimelinessPanel({ t, planInitiated }) {
  const o = t.overall;
  const tone = o.rate === null ? 'text-slate-900' : o.rate >= 95 ? 'text-emerald-600' : o.rate >= 90 ? 'text-amber-600' : 'text-rose-600';
  return (
    <Card title="Timeliness by standard" subtitle="Each case is measured against the clock for its scenario (standards 1.1 and 1.2); the rate is timely over assessed cases.">
      <div className="flex items-center gap-6 mb-4 flex-wrap">
        <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Overall</div><div className={`text-2xl font-bold tracking-tight ${tone}`}>{o.rate === null ? 'n/a' : `${o.rate}%`}</div></div>
        <div className="text-sm text-slate-600">{fmtNum(o.timely)} timely · {fmtNum(o.untimely)} untimely · {fmtNum(o.unmeasured)} unmeasured · {fmtNum(o.notApplicable)} not applicable{o.informational ? ` · ${fmtNum(o.informational)} information only` : ''}</div>
      </div>
      <Table flat head={['Scenario', { label: 'Timely', right: true }, { label: 'Untimely', right: true }, { label: 'Unmeasured', right: true }, { label: 'n/a', right: true }, { label: 'Rate', right: true }]}>
        {t.scenarios.map((s) => (
          <tr key={s.scenario}>
            <td className="px-3 py-2 text-slate-800 text-xs">{s.scenario}</td>
            <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtNum(s.timely)}</td>
            <td className={`px-3 py-2 text-right tabular-nums ${s.untimely ? 'text-rose-600' : 'text-slate-500'}`}>{fmtNum(s.untimely)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtNum(s.unmeasured)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtNum(s.notApplicable + (s.informational || 0))}</td>
            <td className="px-3 py-2 text-right"><Pill tone={s.rate === null ? 'slate' : s.rate >= 95 ? 'emerald' : s.rate >= 90 ? 'amber' : 'rose'}>{s.rate === null ? 'n/a' : `${s.rate}%`}</Pill></td>
          </tr>
        ))}
      </Table>
      {planInitiated > 0 && <div className="text-[11px] text-slate-500 mt-3">{fmtNum(planInitiated)} plan-initiated determination{planInitiated === 1 ? '' : 's'} (V = P) are measured from the initiation date for information only. CMS said in its comment responses that plan-initiated concurrent reviews are not assessed for timeliness but are still sampled for clinical review.</div>}
    </Card>
  );
}

function Results({ result, table, onExport, exporting }) {
  const [ruleFilter, setRuleFilter] = useState('fail');
  const failed = result.rules.filter((r) => r.result === 'fail').length;
  useEffect(() => setRuleFilter(failed ? 'fail' : ''), [failed]);
  return (
    <div className="space-y-4">
      <VerdictBanner result={result} />
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Stat label="Rows" value={fmtNum(result.file.rows)} hint={`${result.file.columns} columns`} />
        <Stat label="Errors" value={fmtNum(result.counts.error)} tone={result.counts.error ? 'rose' : 'emerald'} hint="Rejection or integrity" />
        <Stat label="Findings" value={fmtNum(result.counts.finding)} tone={result.counts.finding ? 'amber' : 'emerald'} hint="Audit conditions" />
        <Stat label="Observations" value={fmtNum(result.counts.observation)} tone="slate" hint="Confirm before submitting" />
        <Stat label="Timeliness" value={result.timeliness.overall.rate === null ? 'n/a' : `${result.timeliness.overall.rate}%`} tone={result.timeliness.overall.rate === null ? 'slate' : result.timeliness.overall.rate >= 95 ? 'emerald' : 'rose'} hint="Assessed cases" />
        <Stat label="Rules failed" value={`${failed} / ${result.rules.length}`} tone={failed ? 'rose' : 'emerald'} hint="Catalog rules" />
      </div>
      <Card title={`${table.protocol} 2027 audit year · Table ${table.number} (${table.abbrev})`} subtitle={table.title}
        right={<button type="button" className={btn.secondary} disabled={exporting} onClick={onExport}>{exporting ? 'Building…' : 'Export results workbook'}</button>}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs text-slate-700">
          <div><div className={labelCls}>Engagement letter</div>{result.context.engagementLetterDate}</div>
          <div><div className={labelCls}>Universe period</div>{result.context.periodStart} to {result.context.periodEnd}<div className="text-[11px] text-slate-500">{result.context.periodSource}</div></div>
          <div><div className={labelCls}>Universe due</div>{result.deadline || 'n/a'}<div className="text-[11px] text-slate-500">15 business days after the letter</div></div>
          <div><div className={labelCls}>File</div>{result.file.name}<div className="text-[11px] text-slate-500">sheet "{result.file.sheet}"</div></div>
        </div>
        <div className="mt-3 text-[11px] text-slate-500">Before uploading to HPMS: zip the .xlsx (CMS requires the Excel file converted to .zip), and keep the sheet exactly as validated.</div>
      </Card>
      <TimelinessPanel t={result.timeliness} planInitiated={result.file.planInitiated} />
      <Card title="Rule results" subtitle={`Every catalog rule for this table with its result on this file. ${result.rules.filter((r) => r.result === 'needs-input').length} need data outside the universe, ${result.rules.filter((r) => r.result === 'manual').length} are case file reviews.`}>
        <RulesTable rules={result.rules} filter={ruleFilter} setFilter={setRuleFilter} />
      </Card>
      {result.issues.length > 0 && <IssuesTable issues={result.issues} total={result.totalIssues} />}
      {(result.universe || []).length > 0 && <UniverseGrid result={result} />}
    </div>
  );
}

function RulesTab({ tables }) {
  const [type, setType] = useState('odag_2029_table1_od');
  useEffect(() => { if (tables.length && !tables.some((t) => t.key === type)) setType(tables[0].key); }, [tables, type]);
  const [rules, setRules] = useState([]);
  const [att, setAtt] = useState(null);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    setError('');
    Promise.all([getJson(`/rules/${type}`), getJson(`/attestation/${type}`).catch(() => null)]).then(([r, a]) => {
      if (!alive) return;
      const ev = new Map(((a && a.rules) || []).map((x) => [x.id, x.evidence]));
      setRules(r.rules.map((x) => ({ ...x, evidence: ev.get(x.id) || '' })));
      setAtt(a);
    }).catch((e) => alive && setError(e.message));
    return () => { alive = false; };
  }, [type]);
  const t = tables.find((x) => x.key === type);
  return (
    <div className="space-y-4">
      <Card title="Rules and attestation" subtitle="Every condition CMS would flag for the table, what the Scrubber does about it, and the test evidence behind it. Implemented rules have a passing and a failing synthetic universe in the automated suite.">
        <div className="flex items-center gap-3 flex-wrap">
          <select className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 bg-white" value={type} onChange={(e) => setType(e.target.value)}>
            {tables.map((x) => <option key={x.key} value={x.key}>{x.protocol} Table {x.number} ({x.abbrev})</option>)}
          </select>
          {t && <span className="text-xs text-slate-500">{t.title}</span>}
          {att && <Pill tone="emerald">Attested {new Date(att.generated).toLocaleString()} · {Object.entries(att.totals).map(([k, v]) => `${STATUS_LABEL[k] || k} ${v}`).join(' · ')}</Pill>}
        </div>
      </Card>
      {error && <Notice tone="red">{error}</Notice>}
      <Card><RulesTable rules={rules} filter={filter} setFilter={setFilter} showEvidence /></Card>
    </div>
  );
}

export default function Scrubber2029App({ user }) {
  const [view, setView] = useState('validate');
  const [tables, setTables] = useState([]);
  const [tierSets, setTierSets] = useState({});
  const [protocol, setProtocol] = useState(null);
  const [table, setTable] = useState(null);
  const [ctx, setCtx] = useState({ engagementLetterDate: '', enrollmentTier: '', periodStart: '', periodEnd: '', submissionDate: todayIso(), contracts: '', aipPbps: '', sldUsed: false });
  const [ctxDone, setCtxDone] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { getJson('/tables').then((d) => { setTables(d.tables || []); setTierSets(d.tierSets || {}); }).catch((e) => setError(e.message)); }, []);

  const step = !protocol ? 0 : !table ? 1 : !ctxDone ? 2 : !result ? 3 : 4;
  const protocols = PROTOCOLS.map((p) => ({ ...p, available: tables.some((t) => t.protocol === p.key) }));
  const proto = protocols.find((p) => p.key === protocol) || null;

  const reset = () => { setTable(null); setCtxDone(false); setFile(null); setResult(null); setError(''); };
  const startOver = () => { setProtocol(null); reset(); };
  const jump = (i) => { if (i === 0) startOver(); else if (i === 1) reset(); else if (i === 2) { setCtxDone(false); setFile(null); setResult(null); } else if (i === 3) { setFile(null); setResult(null); } };

  async function run(f, err) {
    if (err) { setError(err); return; }
    if (!f || !table) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', f, f.name);
      fd.append('universeType', table.key);
      fd.append('context', JSON.stringify(ctx));
      const data = await postForm('/validate', fd);
      setFile(f); setResult(data);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function exportResults() {
    if (!result) return;
    setExporting(true); setError('');
    try { await downloadBlob('/export', { method: 'POST', json: { result }, filename: `${result.universeType}_results.xlsx` }); }
    catch (e) { setError(e.message); } finally { setExporting(false); }
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-slate-800 tracking-tight">Scrubber</h1>
            <p className="text-slate-500 text-sm mt-0.5">CMS-10717 program audit protocols, effective with the 2027 audit year (OMB approval through 10/31/2029). Strict record layout validation: exact headers, exact codes, no stray spaces, every rule with the consequence CMS would attach.</p>
          </div>
          <TabBar value={view} onChange={setView} tabs={[['validate', 'Validate'], ['rules', 'Rules and attestation']]} />
        </div>
        <PhiFriendlyNote variant="ingest" className="mb-4" />

        {view === 'rules' && <RulesTab tables={tables} />}

        {view === 'validate' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <StepChips steps={STEPS} current={step} onJump={jump} />
              {step > 0 && <button type="button" className={btn.link} onClick={startOver}>Start over</button>}
            </div>
            {error && <Notice tone="red">{error}</Notice>}

            {step === 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {protocols.map((p) => (
                  <button key={p.key} type="button" disabled={!p.available} onClick={() => setProtocol(p.key)}
                    className={`text-left bg-white border border-slate-200 rounded-2xl p-4 transition-all ${p.available ? 'hover:shadow-md hover:border-orange-200' : 'opacity-60 cursor-not-allowed'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[13.5px] font-semibold text-slate-900">{p.label}</div>
                      <Pill tone={p.available ? 'emerald' : 'slate'}>{p.available ? 'Available' : 'Next'}</Pill>
                    </div>
                    <div className="mt-1 text-[11.5px] text-slate-500">{p.title}</div>
                    <div className="mt-2 text-[11px] text-slate-500">{TABLE_PLAN[p.key].length} universe table{TABLE_PLAN[p.key].length === 1 ? '' : 's'} · {p.part}</div>
                  </button>
                ))}
              </div>
            )}

            {step === 1 && proto && (
              <Card title={`${proto.label}: ${proto.title}`} subtitle="Pick the universe table the file follows. Tables are enabled one at a time as each one is built and attested." right={<button type="button" className={btn.secondarySmall} onClick={() => setProtocol(null)}>Change protocol</button>}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {TABLE_PLAN[protocol].map((t) => {
                    const live = tables.find((x) => x.key === t.key);
                    return (
                      <button key={t.key} type="button" disabled={!live} onClick={() => live && setTable(live)}
                        className={`text-left bg-white border border-slate-200 rounded-2xl p-4 transition-all ${live ? 'hover:shadow-md hover:border-orange-200' : 'opacity-60 cursor-not-allowed'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-[13.5px] font-semibold text-slate-900">Table {t.number}</div>
                          <div className="flex gap-1"><Pill tone="orange">{t.abbrev}</Pill>{!live && <Pill tone="slate">Next</Pill>}</div>
                        </div>
                        <div className="mt-1 text-[11.5px] text-slate-500">{t.name}</div>
                      </button>
                    );
                  })}
                </div>
              </Card>
            )}

            {step === 2 && <ContextForm ctx={ctx} setCtx={setCtx} tierSets={tierSets} table={table} onContinue={() => setCtxDone(true)} />}

            {step === 3 && (
              <Card title={`${table.protocol} 2027 audit year · Table ${table.number} (${table.abbrev})`} subtitle={table.title} right={<button type="button" className={btn.secondarySmall} onClick={() => setCtxDone(false)}>Change engagement facts</button>}>
                <Dropzone onFile={run} busy={busy} />
                {table.hasSample && (
                  <div className="mt-3 text-[11px] text-slate-500 flex flex-wrap gap-3">
                    <span>Need a file to try? Synthetic samples, no real data:</span>
                    <a className={btn.link} href={`/api/scrubber-2029/sample/${table.key}?which=clean`}>clean universe</a>
                    {(table.samples || []).map((id) => <a key={id} className={btn.link} href={`/api/scrubber-2029/sample/${table.key}?which=${id}`}>{/-T\d+-L1-001$/.test(id) ? 'wrong header' : `failing ${id}`}</a>)}
                  </div>
                )}
              </Card>
            )}

            {step === 4 && result && <Results result={result} table={table} onExport={exportResults} exporting={exporting} />}
            {step === 4 && file && <div className="text-[11px] text-slate-500">Validated {file.name}. <button type="button" className={btn.link} onClick={() => { setFile(null); setResult(null); }}>Validate another file</button></div>}
          </div>
        )}
      </div>
    </div>
  );
}
